import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators} from '@angular/forms';
import {MatCheckboxChange} from '@angular/material/checkbox';
import {MatDialog} from '@angular/material/dialog';
import {MatExpansionPanel} from '@angular/material/expansion';
import {AddRemarkPopup} from '../../../../popups/add-remark-popup/add-remark-popup.component';
import {ConfirmPopupDialog} from '../../../../popups/confirm-popup/confirm-popup.component';
import {
    IPackage_ProductAllocation,
    IProduct_ProductAllocation,
    PackingTypeNew,
    UnitsOfMeasure
} from '../../product-allocation-new.component';
import {moreThen} from '../pack-items/pack-items.component';

export function productsItemValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        if (!control.value?.productId) {
            return {
                empty: true
            }
        }
        return null;
    };
}

@Component({
    selector: 'eci-carton-item',
    templateUrl: './carton-item.component.html',
    styleUrls: ['./carton-item.component.scss']
})
export class CartonItemComponent implements OnInit, OnChanges {

    @Input() package: IPackage_ProductAllocation;
    @Output() packageChange: EventEmitter<IPackage_ProductAllocation> = new EventEmitter<IPackage_ProductAllocation>();

    @Input() isSimply: boolean = false;
    @Input() notAllocated: number = 0;
    @Input() denominator: number = 1;
    @Input() canCancel: boolean = false;
    @Input() products: IProduct_ProductAllocation[] = [];
    @Input() isViewing: boolean = false;
    @Input() selected: boolean = false;
    @Input() namePrefix: string = '';
    @Output() onCancelEvent: EventEmitter<IPackage_ProductAllocation> = new EventEmitter<IPackage_ProductAllocation>();
    @Output() onQuantityChange: EventEmitter<any> = new EventEmitter<any>();
    @Output() onDuplicateChange: EventEmitter<any> = new EventEmitter<any>();
    @Output() selectionChange: EventEmitter<MatCheckboxChange> = new EventEmitter<MatCheckboxChange>();

    @ViewChild(MatExpansionPanel) matExpansionPanel: MatExpansionPanel;


    cartonForm: FormGroup;
    validate = false;
    searchProduct: FormControl = new FormControl('');


    constructor(
        private formBuilder: FormBuilder,
        private dialog: MatDialog,
    ) {}

    get allProducts() {
        return this.products.filter(p => {
            return p.description.toLowerCase().includes(this.searchProduct.value.toLowerCase()) || p.itemNumber.toLowerCase().includes(this.searchProduct.value.toLowerCase());
        });
    }

    get productsInPackage() {
        return (this.cartonForm.get('products') as FormArray).length;
    }

    get productQuantity() {
        return (this.cartonForm.get('products') as FormArray).value.reduce((sum, val) => {
            return sum + val.allocated;
        }, 0);
    }

    get cartonProducts() {
        return this.cartonForm.controls['products'] as FormArray;
    }

    ngOnInit(): void {
        this.cartonForm = this.formBuilder.group({
            length: [{value: this.package.length, disabled: this.isViewing}, {validators: [Validators.required, moreThen()]}],
            width: [{value: this.package.width, disabled: this.isViewing}, {validators: [Validators.required, moreThen()]}],
            height: [{value: this.package.height, disabled: this.isViewing}, {validators: [Validators.required, moreThen()]}],
            grossWeight: [{value: this.package.grossWeight, disabled: this.isViewing}, {validators: [Validators.required, moreThen()]}],
            volumetricWeight: [this.package.volumetricWeight],
        });

        const products = this.formBuilder.array([]);
        this.cartonForm.addControl('products', products);
        this.subscribeProducts();
        this.formatProducts();

        this.cartonForm.get('length').valueChanges.subscribe(values => this.onChangeInfo());
        this.cartonForm.get('width').valueChanges.subscribe(values => this.onChangeInfo());
        this.cartonForm.get('height').valueChanges.subscribe(values => this.onChangeInfo());
    }

    formatProducts(withClear: boolean = false) {
        if (this.isSimply) {
            this.package.products = [{
                allocated: withClear ?
                    null :
                    (this.package.products[0]?.allocated ? this.package.products[0].allocated : this.package.productsQuantity)
            }]
        } else {
            this.package.products = this.package.products.map(p => {
                const productId = p.item?.productId || (p as any).productId;
                const found = productId ? this.products.find(_p => {
                    if (_p.product) {
                        return _p.product?.id === productId;
                    } else {
                        return _p.productId === productId;
                    }
                }) : null;
                if ((p.hasOwnProperty('item') && p.hasOwnProperty('allocated'))) {
                    if (found) {
                        p.item = found;
                    }
                    return p;
                } else {
                    return {
                        item: withClear ? null : found,
                        allocated: withClear ? null : ((p as any).quantity || p.allocated)
                    }
                }
            })
        }

        if (this.cartonForm) {
            (this.cartonForm.get('products') as FormArray).clear();
            for (const product of this.package.products) {
                const prodForm = this.isSimply ? this.formBuilder.group({
                    allocated: [{value: product.allocated, disabled: this.isViewing}, {
                        validators: [
                            Validators.required,
                            Validators.min(1),
                            Validators.pattern(/^[0-9]*$/),
                        ]
                    }],
                }) : this.formBuilder.group({
                    item: [{value: product.item, disabled: this.isViewing}, {
                        validators: [
                            Validators.required,
                            productsItemValidator()
                        ]}],
                    allocated: [{value: product.allocated, disabled: this.isViewing}, {
                        validators: [
                            Validators.required,
                            Validators.min(1),
                            Validators.pattern(/^[0-9]*$/),
                        ]
                    }],
                });
                (this.cartonForm.get('products') as FormArray).push(prodForm);
            }
        }

    }

    subscribeProducts() {
        this.cartonForm.get('products').valueChanges.subscribe(value => {
            if (this.isSimply) {
                if (value.length > 0) {
                    this.onQuantityChange.emit({
                        name: this.namePrefix + this.getType() + ' ' + this.package.index,
                        value: value[0].allocated ? value[0].allocated : 0
                    })
                }
            } else {
                const res = (value as any[]).filter(i => i.item?.productId);
                if (res.length > 0) {
                    this.onQuantityChange.emit({
                        name: this.namePrefix + this.getType() + ' ' + this.package.index,
                        value: res.map(i => {
                            return {[i.item.productId]: i.allocated != null ? i.allocated : 0}
                        })
                    })
                }
            }
            this.package.productsQuantity = this.productQuantity;
            this.onChangeInfo();
            setTimeout(() => {
                if (this.isSimply) {
                    const control = this.cartonProducts.at(0)?.get('allocated');
                    if (!control) {
                        return;
                    }
                    const errors = control.errors ? {...control.errors} : {};
                    delete errors.overflow;
                    if (value[0]?.allocated != null && this.notAllocated < 0) {
                        errors.overflow = true;
                    }
                    control.setErrors(Object.keys(errors).length ? errors : null);
                } else {
                    (value as any[]).forEach((v, i) => {
                        const control = this.cartonProducts.at(i)?.get('allocated');
                        if (!control || !v.item?.productId) {
                            return;
                        }
                        const product = this.products.find(p => p.productId === v.item.productId);
                        const errors = control.errors ? {...control.errors} : {};
                        delete errors.overflow;
                        if (v.allocated != null && product && product.notAllocated < 0) {
                            errors.overflow = true;
                        }
                        control.setErrors(Object.keys(errors).length ? errors : null);
                    });
                }
            }, 0);
        });
    }

    addChild() {
        const prodForm = this.formBuilder.group({
            item: [{}, {
                validators: [
                    Validators.required,
                    productsItemValidator()
                ]}],
            allocated: [null, {
                validators: [
                    Validators.required,
                    Validators.pattern(/^[0-9]*$/),
                ]}]
        });
        (this.cartonForm.get('products') as FormArray).push(prodForm);
    }

    onSave() {
        if (this.validatePackage()) {
            this.package = {
                ...this.package,
                ...this.cartonForm.value,
                productsQuantity: this.productQuantity,
                expand: false,
                isNew: false
            };
            this.packageChange.emit(this.package);
            this.matExpansionPanel.close();
        }
    }

    onCancel() {
        this.onCancelEvent.emit(this.package);
    }

    onPanelClosed() {
        if (this.package.expand && !this.isViewing) {
            this.matExpansionPanel.open();
        }
    }

    private validatePackage() {
        this.validate = true;
        return this.cartonForm.valid;
    }

    onChangeInfo() {
        this.cartonForm.get('volumetricWeight').setValue(this.calculateVolumetricWeight());
    }

    deleteChild(index: number) {
        (this.cartonForm.get('products') as FormArray).removeAt(index);
    }

    calculateVolumetricWeight() {
        const volumetricWeight =
            this.cartonForm.get('height').value *
            this.cartonForm.get('width').value *
            this.cartonForm.get('length').value / this.denominator;
        return parseFloat(volumetricWeight.toFixed(3));
    }

    getType() {
        switch (this.package.packingType) {
            case PackingTypeNew.single_carton:
                return `Single carton`;
            case PackingTypeNew.carton:
                return `Carton`;
        }
    }

    checkOptionDisabled(p: IProduct_ProductAllocation) {
        let ids = this.cartonProducts.value.map(v => v.item?.productId);
        return ids.includes(p.productId);
    }

    compareProducts(p1, p2) {
        return p1 && p2 && p1.productId === p2.productId;
    }

    checkAddBtnDisabled() {
        return this.cartonProducts.length === this.products.length;
    }

    onDuplicate(e: MouseEvent) {
        e.stopPropagation();
        e.preventDefault();
        const dialogRef = this.dialog.open(AddRemarkPopup, {width: '400px'});
        dialogRef.componentInstance.dialogTitle = 'Duplicate carton';
        dialogRef.componentInstance.fieldLabel = 'Number of copies';
        dialogRef.componentInstance.numericOnly = true;
        dialogRef.afterOpened().subscribe(() => {
            dialogRef.componentInstance.form.patchValue({remark: '1'});
        });
        dialogRef.afterClosed().subscribe(value => {
            const count = parseInt(value, 10);
            if (!count || count < 1) {
                return;
            }
            if (this.isSimply) {
                const qty = this.package.products?.[0]?.allocated || this.package.productsQuantity || 0;
                if (count * qty > this.notAllocated) {
                    const errRef = this.dialog.open(ConfirmPopupDialog, {width: '400px'});
                    errRef.componentInstance.header = 'Cannot duplicate';
                    errRef.componentInstance.message = `Only ${this.notAllocated} allocated product quantity available.`;
                    errRef.componentInstance.showRightBtn = false;
                    errRef.componentInstance.leftBtnText = 'OK';
                    return;
                }
            } else {
                for (const p of this.package.products || []) {
                    if (!p.item?.productId) {
                        continue;
                    }
                    const product = this.products.find(pr => pr.productId === p.item.productId);
                    const needed = count * (p.allocated || 0);
                    if (product && needed > product.notAllocated) {
                        const errRef = this.dialog.open(ConfirmPopupDialog, {width: '400px'});
                        errRef.componentInstance.header = 'Cannot duplicate';
                        errRef.componentInstance.message = `Not enough units for ${product.description || product.itemNumber}. Available: ${product.notAllocated}, needed: ${needed}.`;
                        errRef.componentInstance.showRightBtn = false;
                        errRef.componentInstance.leftBtnText = 'OK';
                        return;
                    }
                }
            }
            this.onDuplicateChange.emit({package: this.package, count});
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.isSimply && changes.isSimply.firstChange == false) {
            this.formatProducts();
        }
    }

    getUnit(unitsOfMeasure: number) {
        return UnitsOfMeasure[unitsOfMeasure];
    }

}
